/**
 * DL Fork: BI bridge — pure protocol helpers.
 *
 * The bidirectional host-app<->iframe postMessage protocol that lets the
 * external MCP assistant (via the host application) deep-link into BI
 * dashboards/charts, ask the open dashboard to refresh after a write, and
 * read back the screen the operator is looking at.
 *
 * This module holds the PURE, side-effect-free pieces (target/uuid validation,
 * path composition, pathname→context derivation, message type guards) so they
 * can be unit-tested in isolation. The React wiring (listeners, emitter,
 * activation gate) lives in `DlBiBridge.tsx` / `useBiBridge.ts`.
 *
 * Wire contract (session-auth C0):
 *   inbound  {type:'dl:navigate', target:'dashboard'|'chart'|'space'|'home', uuid?}
 *   inbound  {type:'dl:data-changed'}
 *   outbound {type:'dl:context', projectUuid, dashboardUuid?, chartUuid?,
 *             spaceUuid?, mode?, name?}
 * Unknown message types are ignored (forward-compatibility).
 */
import { matchPath } from 'react-router';

/** Internal window event the root listener relays `dl:data-changed` through. */
export const DL_INTERNAL_DATA_CHANGED_EVENT = 'dl:internal-data-changed';

/** Positive allowlist of inbound navigation targets. */
export const DL_NAV_TARGETS = ['dashboard', 'chart', 'space', 'home'] as const;
export type DlNavTarget = (typeof DL_NAV_TARGETS)[number];

/**
 * UUID v4-shape check. `dl:navigate` uuids must match this before ANY use — the
 * only exception is `target:'home'`, which carries no uuid (PRD §4 invariant 3).
 */
const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidUuid(value: unknown): value is string {
    return typeof value === 'string' && UUID_RE.test(value);
}

export function isDlNavTarget(value: unknown): value is DlNavTarget {
    return (
        typeof value === 'string' &&
        (DL_NAV_TARGETS as readonly string[]).includes(value)
    );
}

/** Inbound message shapes (only the fields we consume). */
export type DlNavigateMessage = {
    type: 'dl:navigate';
    target: DlNavTarget;
    uuid?: string;
};

export type DlDataChangedMessage = { type: 'dl:data-changed' };

/**
 * Outbound context payload. `name` is best-effort (may be absent) — everything
 * else is derived synchronously from the pathname.
 */
export type DlContextMessage = {
    type: 'dl:context';
    projectUuid: string;
    dashboardUuid?: string;
    chartUuid?: string;
    spaceUuid?: string;
    mode?: string;
    name?: string;
};

/**
 * Validate a raw inbound `dl:navigate` request. Returns the typed request when
 * it is a recognised target with a well-formed uuid (or `home`, which needs
 * none), else `null`. Fail-closed: anything malformed is dropped.
 */
export function parseNavigateRequest(data: unknown): DlNavigateMessage | null {
    if (typeof data !== 'object' || data === null) return null;
    const record = data as Record<string, unknown>;
    if (record.type !== 'dl:navigate') return null;
    if (!isDlNavTarget(record.target)) return null;

    if (record.target === 'home') {
        return { type: 'dl:navigate', target: 'home' };
    }

    if (!isValidUuid(record.uuid)) return null;
    return { type: 'dl:navigate', target: record.target, uuid: record.uuid };
}

/**
 * Compose the in-app route for a validated navigate request. The fork owns path
 * composition — no free-form path ever crosses the boundary (PRD §4 invariant
 * 3). Returns `null` if inputs are missing/invalid (defensive; callers should
 * already have validated via `parseNavigateRequest`).
 */
export function composeNavPath(
    projectUuid: string,
    request: Pick<DlNavigateMessage, 'target' | 'uuid'>,
): string | null {
    if (!projectUuid) return null;
    const base = `/projects/${projectUuid}`;
    switch (request.target) {
        case 'home':
            return `${base}/home`;
        case 'dashboard':
            return isValidUuid(request.uuid)
                ? `${base}/dashboards/${request.uuid}`
                : null;
        case 'chart':
            return isValidUuid(request.uuid)
                ? `${base}/saved/${request.uuid}`
                : null;
        case 'space':
            return isValidUuid(request.uuid)
                ? `${base}/spaces/${request.uuid}`
                : null;
        default:
            return null;
    }
}

/**
 * Derive the outbound `dl:context` (minus `name`) from a pathname. Matches the
 * dashboard / saved-chart / space / home route patterns; returns `null` for any
 * other screen (so the emitter stays silent off-BI-content pages). `mode` is
 * the optional dashboard/chart trailing segment (`view`/`edit`).
 */
export type DlBiContext = Omit<DlContextMessage, 'type' | 'name'> & {
    screen: 'dashboard' | 'chart' | 'space' | 'home';
};

export function deriveBiContext(pathname: string): DlBiContext | null {
    const dashboard = matchPath(
        '/projects/:projectUuid/dashboards/:dashboardUuid/:mode?',
        pathname,
    );
    if (dashboard?.params.projectUuid && dashboard.params.dashboardUuid) {
        return {
            screen: 'dashboard',
            projectUuid: dashboard.params.projectUuid,
            dashboardUuid: dashboard.params.dashboardUuid,
            mode: dashboard.params.mode ?? undefined,
        };
    }

    // Tabbed dashboard route — same screen/entity, deeper path.
    const dashboardTab = matchPath(
        '/projects/:projectUuid/dashboards/:dashboardUuid/:mode/tabs/:tabUuid?',
        pathname,
    );
    if (
        dashboardTab?.params.projectUuid &&
        dashboardTab.params.dashboardUuid
    ) {
        return {
            screen: 'dashboard',
            projectUuid: dashboardTab.params.projectUuid,
            dashboardUuid: dashboardTab.params.dashboardUuid,
            mode: dashboardTab.params.mode ?? undefined,
        };
    }

    const chart = matchPath(
        '/projects/:projectUuid/saved/:savedQueryUuid/:mode?',
        pathname,
    );
    if (chart?.params.projectUuid && chart.params.savedQueryUuid) {
        return {
            screen: 'chart',
            projectUuid: chart.params.projectUuid,
            chartUuid: chart.params.savedQueryUuid,
            mode: chart.params.mode ?? undefined,
        };
    }

    const space = matchPath(
        '/projects/:projectUuid/spaces/:spaceUuid',
        pathname,
    );
    if (space?.params.projectUuid && space.params.spaceUuid) {
        return {
            screen: 'space',
            projectUuid: space.params.projectUuid,
            spaceUuid: space.params.spaceUuid,
        };
    }

    const home = matchPath('/projects/:projectUuid/home', pathname);
    if (home?.params.projectUuid) {
        return { screen: 'home', projectUuid: home.params.projectUuid };
    }

    return null;
}
